import { useEffect, useMemo, useRef } from 'react';
import { ROUTES } from '../routing/routes';
import { AuthStatus, useAuth } from './Auth';
import { useNetworkStatus } from './useNetworkStatus';
import { OnboardingProgress } from '../../types/obsessless-user';
import { useOnboardingStore } from '../../pages/onboarding/hooks/useOnboardingStore';

export enum AppStatus {
  Offline = 'offline',
  Loading = 'loading',
  Ready = 'ready',
}

type UseAppBootstrapReturn = {
  appStatus: AppStatus;
  entryRoute: string | undefined;
  isAuthenticated: boolean;
};

export const useAppBootstrap = (): UseAppBootstrapReturn => {
  const { authStatus, readyUser } = useAuth();
  const isOnline = useNetworkStatus();
  const isAuthenticated = authStatus === AuthStatus.AuthenticatedReady;
  const isUnauthenticated = authStatus === AuthStatus.Unauthenticated;
  const isInitializing = authStatus === AuthStatus.Initializing;
  const isAuthenticatedLoading = authStatus === AuthStatus.AuthenticatedLoading;

  const whatBringsYouResponse = useOnboardingStore(state => state.whatBringsYouResponse);
  const hasWhatBringsYouResponse = whatBringsYouResponse.length > 0;

  // Once the app has been Ready, don't blank the screen for background auth
  // transitions (e.g. anonymous sign-in during onboarding).
  const hasBeenReady = useRef(false);

  const appStatus: AppStatus = useMemo(() => {
    if (!isOnline) return AppStatus.Offline;
    if (isInitializing) return AppStatus.Loading;
    if (isAuthenticatedLoading && !hasBeenReady.current) return AppStatus.Loading;
    return AppStatus.Ready;
  }, [isOnline, authStatus]);

  useEffect(() => {
    if (appStatus === AppStatus.Ready) {
      hasBeenReady.current = true;
    }
  }, [appStatus]);

  const entryRoute = useMemo(() => {
    if (isInitializing) {
      return undefined;
    }

    // During background auth after the app was already showing content,
    // return a placeholder route so App doesn't blank the screen.
    // The URL already determines which page the user sees.
    if (isAuthenticatedLoading && hasBeenReady.current) {
      return ROUTES.onboarding.whatBringsYou;
    }

    if (isAuthenticatedLoading) {
      return undefined;
    }

    if (isUnauthenticated || !readyUser) {
      return ROUTES.onboarding.whatBringsYou;
    }

    // if we get here we are authenticated and have a what brings you response so need to check if we have finished onboarding

    if (!readyUser.hasFinishedOnboarding) {
      let route: string;
      switch (readyUser.onboardingProgress) {
        case OnboardingProgress.DrawnHeart:
          route = ROUTES.onboarding.ocdThemesSelection;
          break;
        case OnboardingProgress.AnsweredOcdThemeQuestions:
          route = ROUTES.onboarding.personalizingOllie;
          break;
        case OnboardingProgress.GaveNotificationPermissions:
          route = ROUTES.onboarding.highFive;
          break;
        case OnboardingProgress.GaveRating:
          route = ROUTES.onboarding.ocdCycleIntro;
          break;
        case OnboardingProgress.FinishedOnboarding:
          route = ROUTES.onboarding.goToAppStore;
          break;
        default:
          // For users with no progress (including newly authenticated anonymous users), start at WhatBringsYou
          route = ROUTES.onboarding.whatBringsYou;
      }
      //console.log('[useAppBootstrap] Onboarding not finished, route:', route, 'progress:', readyUser.onboardingProgress);
      return route;
    }
    if (!readyUser.isProUser) {
      //console.log('[useAppBootstrap] Showing paywall');
      return ROUTES.onboarding.paywall;
    }
    //console.log('[useAppBootstrap] Returning root');
    return ROUTES.onboarding.goToAppStore;
  }, [authStatus, readyUser]);

  //console.log('[useAppBootstrap] Final values:', { appStatus, entryRoute, isAuthenticated, readyUser: readyUser?.uid });

  return { appStatus, entryRoute, isAuthenticated };
};
